import { createEntityAdapter } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { Player, PlayersState } from './players.state';
import {
  ChromeComponentActions,
  EditPlayerFormActions,
  PlayerApiActions,
  PlayerDetailsPageActions,
  PlayerListPageActions,
} from './players.actions';
import { errorState, loadedState, loadingState } from 'ngx-http-request-state';

type PartialPlayerWithId = Partial<Omit<Player, 'id'>> & Pick<Player, 'id'>;

const adapter = createEntityAdapter<Player>({
  selectId: (p) => p.id,
  sortComparer: (a, b) =>
    a.displayName.localeCompare(b.displayName) ||
    a.bbUserName.localeCompare(b.bbUserName),
});

const initialState: PlayersState = {
  ...adapter.getInitialState(),
  initialLoadRequestError: undefined,
  initialLoadRequestComplete: false,
  myPlayerId: undefined,
  focusedProfile: undefined,
};

export const playersFeature = createFeature({
  name: 'players',
  reducer: createReducer(
    initialState,
    on(ChromeComponentActions.loadPlayers, (state) => ({
      ...state,
      initialLoadRequestError: undefined,
      initialLoadRequestComplete: false,
    })),
    on(PlayerApiActions.initialPlayerLoadFailed, (state, { error }) => ({
      ...state,
      initialLoadRequestError: error,
    })),
    on(PlayerDetailsPageActions.closePlayerProfile, (state) => ({
      ...state,
      focusedProfile: initialState.focusedProfile,
    })),
    on(PlayerApiActions.profileLoading, (state, { profile }) => ({
      ...state,
      focusedProfile: loadingState(profile),
    })),
    on(PlayerApiActions.profileLoadSuccess, (state, { profile }) => ({
      ...state,
      focusedProfile: loadedState(profile),
    })),
    on(PlayerApiActions.profileLoadError, (state, { error, fallback }) => ({
      ...state,
      focusedProfile: errorState(error, fallback),
    })),
    on(PlayerApiActions.playerNotFound, (state, { fallback }) => ({
      ...state,
      focusedProfile: loadedState(fallback),
    })),
    on(
      PlayerDetailsPageActions.playerDetailsLoaded,
      EditPlayerFormActions.playerDetailsUpdated,
      PlayerApiActions.playerLoaded,
      (state, { player }) => ({
        ...adapter.upsertOne(player, state),
        myPlayerId: player.isMe ? player.id : state.myPlayerId,
        focusedProfile:
          state.focusedProfile?.value.bbUserName === player.bbUserName
            ? {
                ...state.focusedProfile,
                value: {
                  ...state.focusedProfile.value,
                  ...player,
                },
              }
            : state.focusedProfile,
      }),
    ),
    on(
      PlayerListPageActions.playerDetailsLoaded,
      PlayerApiActions.playersLoaded,
      (state, { players }) => ({
        ...adapter.upsertMany(players, state),
        initialLoadRequestComplete: true,
        initialLoadRequestError: undefined,
        myPlayerId: players.find((p) => p.isMe)?.id,
      }),
    ),
  ),
  extraSelectors: ({ selectPlayersState }) => {
    const adapterSelectors = adapter.getSelectors(selectPlayersState);
    return {
      ...adapterSelectors,
      selectPlayerById: (id: string) =>
        createSelector(
          adapterSelectors.selectEntities,
          (entities) => entities[id],
        ),
      selectPlayerByBbUserName: (bbUserName: string) =>
        createSelector(adapterSelectors.selectAll, (entities) =>
          entities.find((player) => player.bbUserName === bbUserName),
        ),
      selectPlayersByIds: (ids: string[]) =>
        createSelector(adapterSelectors.selectEntities, (entities) =>
          ids
            .map<PartialPlayerWithId>((id) => entities[id] || { id })
            .sort((a, b) =>
              (a?.displayName ?? '').localeCompare(b?.displayName ?? ''),
            ),
        ),
    };
  },
});
